import { useEffect, useState, useMemo } from "react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Bell,
  LogOut,
  User,
  MessageSquare,
  Users,
  Reply,
  CheckCircle,
  TrendingUp,
  UserCog,
  Check,
} from "lucide-react";
// import { NetworkStatusBadge } from "./NetworkStatusBadge";
import { useAuth } from "../App";
import { ROLES } from "../constant/roles";
import { useNetworkStatusContext } from "../contexts/NetworkStatusContext";
// import useAuth from "./Context/AuthContext";
// import useNavigation from "./Context/NavigationContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Card, CardContent } from "./ui/card";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { tokenService } from "../services/TokenService";
import { useNavigate, useLocation } from "react-router-dom";
import {
  useNotificationsQuery,
  useNotificationCountQuery,
  useMarkNotificationsAllReadMutation,
} from "../api/CommunicationApi";
import paths from "../routes";
import { useStopSandboxMutation } from "../api/jupyterManagementApi";
import { useJupyterWorkspace } from "../contexts/JupyterWorkspaceContext";
import { API_CONFIG } from "../config/app.config";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import Logo from "../assets/data-lab-logo.png";
import { getConfiguration } from "../api/settingsApi";
import getBITSEndApiMap from "../apiMap";

interface Notification {
  id: string;
  type: "announcement" | "message" | "forum_reply" | "feedback" | "mention";
  title: string;
  content: string;
  sourceId: string;
  sourceType: string;
  createdAt: string;
  isRead: boolean;
  actionUrl?: string;
  conversationId?: string;
  announcementId?: string;
}
function timeAgo(date: string) {
  const now = new Date();
  const past = new Date(date);
  const diffMs = now.getTime() - past.getTime();
  const diffInHours = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffInHours < 1) return "Just now";
  if (diffInHours < 24) return `${diffInHours}h ago`;
  return `${Math.floor(diffInHours / 24)}d ago`;
}

function getNotificationIcon(t?: string | null) {
  const iconStyle: React.CSSProperties = { width: "18px", height: "24px" };
  const type = (t ?? "").toLowerCase();

  switch (type) {
    case "announcement":
      return <Bell style={iconStyle} className="text-bits-blue" />;
    case "message":
      return <MessageSquare style={iconStyle} className="text-orange-500" />;
    case "forumreply":
    case "forum_reply":
    case "forum":
      return <Reply style={iconStyle} className="text-green-600" />;
    case "feedback":
      return <CheckCircle style={iconStyle} className="text-purple-600" />;
    case "mention":
      return <Users style={iconStyle} className="text-bits-red" />;
    default:
      return <Bell style={iconStyle} />;
  }
}

export function NotificationDropdown() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const { setIsOnline } = useNetworkStatusContext();
  const markAllMutation = useMarkNotificationsAllReadMutation();

  const queryClient = useQueryClient();

  // Unread count for badge
  const {
    data: unreadResp,
    isLoading: unreadLoading,
    isError: unreadError,
  } = useNotificationCountQuery({
    enabled: true,
    refetchOnWindowFocus: true,
    staleTime: 10000, 
  });

  // Setup SSE for live count updates
  useEffect(() => {
    const token = tokenService.getToken();
    if (!token) return;

    const streamUrl = `${getBITSEndApiMap("notificationsStream")}?token=${token}`;
    const eventSource = new EventSource(streamUrl);

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data && typeof data.total === 'number') {
          // Update the react query cache directly to prevent flickering
          queryClient.setQueryData(["notifications", "count"], (oldData: any) => {
            return {
              ...oldData,
              isSuccess: true,
              data: {
                ...oldData?.data,
                ...data,
              }
            };
          });
        }
      } catch (e) {
        console.error("SSE parse error", e);
      }
    };

    eventSource.addEventListener("refetch", () => {
      queryClient.invalidateQueries({ queryKey: ["notifications", "count"] });
    });

    eventSource.onerror = (err) => {
      console.error("EventSource failed:", err);
      // EventSource automatically attempts to reconnect, but we can set online status
      setIsOnline(false);
    };

    eventSource.onopen = () => {
      setIsOnline(true);
    };

    return () => {
      eventSource.close();
    };
  }, [queryClient, setIsOnline]);

  // Detect network status from notification count query error
  useEffect(() => {
    if (unreadError) {
      setIsOnline(false);
    } else {
      setIsOnline(true);
    }
  }, [unreadError, setIsOnline]);

  const unreadCount =
    (unreadResp as any)?.data?.total ??
    (unreadResp as any)?.data?.count ??
    (unreadResp as any)?.total ??
    (unreadResp as any)?.count ??
    0;

  // Notifications list (use small limit to keep dropdown concise)
  const {
    data: listResp,
    isLoading: listLoading,
    isError: listError,
  } = useNotificationsQuery(
    { page: 1, limit: 5 },
    {
      enabled: isOpen, // only fetch when the menu is open
      refetchInterval: isOpen ? 10000 : false,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: true,
      staleTime: 0,
    },
  );

  const recentNotifications: Notification[] = useMemo(() => {
    const items =
      (listResp as any)?.data?.notifications ??
      (listResp as any)?.notifications ??
      (listResp as any)?.data ??
      [];
    return (items as any[]).map((n) => ({
      id: n.id,
      type: n.type as Notification["type"],
      title: n.title ?? "Notification",
      content: n.content ?? "",
      sourceId: n.sourceId ?? "",
      sourceType: n.sourceType ?? "",
      createdAt: n.createdAt,
      isRead: !!n.isRead,
      // unify meta/metadata naming
      metadata: n.meta ?? n.metadata ?? {},
    }));
  }, [listResp]);

  // Navigation mapping based on metadata seen in CommunicationApi
  const handleNotificationClick = (n: Notification) => {
    const type = (n.type ?? "").toLowerCase();
    const meta = (n as any).metadata ?? {};

    // Keep this mapping aligned with your app routes and metadata keys
    if (type === "announcement") {
      const announcementId = meta.announcementId ?? meta.id ?? n.id;
      navigate(paths.communication, {
        state: { defaultTab: "announcements", announcementId, from: location.pathname },
      });
    } else if (type === "message") {
      const conversationId =
        meta.conversationId ?? meta.threadId ?? meta.sourceId ?? n.id;
      navigate(paths.communication, {
        state: { defaultTab: "messages", conversationId, from: location.pathname },
      });
    } else if (
      type === "forumreply" ||
      type === "forum_reply" ||
      type === "forum" ||
      type === "mention"
    ) {
      const threadId = meta.threadId ?? meta.sourceId ?? n.id;
      navigate(paths.communication, {
        state: { defaultTab: "messages", conversationId: threadId, from: location.pathname },
      });
    } else if (type === "feedback") {
      const projectId = meta.projectId ?? meta.sourceId ?? n.id;
      navigate(
        `${paths["project-details"]}?id=${encodeURIComponent(projectId)}`,
      );
    } else {
      navigate(paths.communication, {
        state: { from: location.pathname },
      });
    }

    setIsOpen(false);
  };

  const markAllAsRead = async () => {
    const items = recentNotifications
      .filter((n) => !n.isRead)
      .map((n) => {
        const t = (n.type ?? "").toLowerCase();
        const type: "message" | "announcement" =
          t === "announcement" ? "announcement" : "message";
        return { type, id: n.id };
      })
      .filter((i) => i.type === "message" || i.type === "announcement");

    if (items.length === 0) return;

    try {
      await markAllMutation.mutateAsync({ items });
    } catch (e) {
      toast.error("Mark all as read failed");
    }
  };

  const empty = !listLoading && !listError && recentNotifications?.length === 0;

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="relative dark:border-[#30363d] dark:text-[#c9d1d9] dark:hover:bg-[#30363d]">
          <Bell className="h-4 w-4" />
          {!unreadLoading && !unreadError && unreadCount > 0 && (
            <Badge className="absolute -top-2 -right-2 bg-red-500 text-white text-xs h-5 w-5 flex items-center justify-center p-0">
              {unreadCount > 9 ? "9+" : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="p-0 border border-gray-300 dark:border-[#30363d] rounded-lg shadow-md w-auto max-w-md dark:bg-[#161b22]"
      >
        <DropdownMenuLabel className="flex items-center justify-between pl-5 py-4 border-b border-gray-300 dark:border-[#30363d] rounded-t-lg mb-2">
          <span className="font-inter font-semibold text-xl text-gray-900 dark:text-[#f0f6fc]">
            Notification
          </span>
          <Button
            variant="outline"
            size="sm"
            className="font-inter font-semibold text-sm text-gray-600 dark:text-[#8b949e] hover:text-gray-800 dark:hover:text-[#f0f6fc] dark:border-[#30363d] dark:bg-[#0d1117]"
            onClick={markAllAsRead}
            disabled={
              !recentNotifications.some((n) => !n.isRead) ||
              markAllMutation.isPending
            }
            title={
              empty ? "No notifications" : "Mark all Notifications as read"
            }
          >
            <Check className="h-3 w-3 " />
            Mark all as read
          </Button>
        </DropdownMenuLabel>

        {listLoading ? (
          <div className="p-4 text-center text-muted-foreground dark:text-[#8b949e] text-sm">
            Loading...
          </div>
        ) : listError ? (
          <div className="p-4 text-center text-red-600 dark:text-red-400 text-sm">
            Failed to load notifications
          </div>
        ) : empty ? (
          <div className="p-4 text-center text-muted-foreground dark:text-[#8b949e] text-sm">
            No notifications
          </div>
        ) : (
          recentNotifications?.map((n) => (
            <div key={n.id}>
              <DropdownMenuItem
                className="p-0 cursor-pointer focus:bg-transparent focus:text-inherit
             focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
                onClick={() => handleNotificationClick(n)}
              >
                <Card className="w-full border-0 shadow-none hover:bg-gray-50 dark:hover:bg-[#21262d] dark:bg-transparent">
                  <CardContent className="px-1 py-1 flex items-start gap-3">
                    <div className="flex items-center justify-center w-12 h-12 flex-shrink-0">
                      {getNotificationIcon(n.type)}
                    </div>
                    <div className="flex-1 min-w-0 mr-4">
                      <div className="flex items-center justify-between mb-1">
                        <p className="font-inter font-medium text-base text-gray-800 dark:text-[#f0f6fc] truncate">
                          {n.title ?? "Notification"}
                        </p>
                      </div>
                      <p className="font-inter font-normal text-sm text-gray-600 dark:text-[#8b949e] line-clamp-2">
                        {n.content ?? ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 mr-2 flex-shrink-0">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {timeAgo(n.createdAt)}
                      </span>
                      {!n.isRead && (
                        <div className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0" />
                      )}
                    </div>
                  </CardContent>
                </Card>
              </DropdownMenuItem>
            </div>
          ))
        )}
        <DropdownMenuItem
          className="flex items-center justify-center gap-1 px-2 py-3 border-t border-gray-300 dark:border-[#30363d] rounded-b-lg
          cursor-pointer hover:bg-gray-50 dark:hover:bg-[#21262d] focus:bg-gray-50 dark:focus:bg-[#21262d] focus:text-inherit
          focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
          onClick={() =>
            navigate(paths.communication, {
              state: { defaultTab: "announcements", from: location.pathname },
            })
          }
        >
          <MessageSquare className="h-6 w-6 text-gray-800 dark:text-[#c9d1d9]" />
          <span className="font-inter font-medium text-lg text-gray-800 dark:text-[#c9d1d9]">
            View all communication
          </span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Header() {
  const { user, logout } = useAuth();
  const [accessToken, setAccessToken] = useState<string>("");
  const { workspace, resetWorkspace } = useJupyterWorkspace();
  const stopSanboxMutation = useStopSandboxMutation();

  const configurationQuery = useQuery({
    queryKey: ["/settings"],
    queryFn: getConfiguration,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    // prefill from storage
    setAccessToken(tokenService.getToken() ?? "");
  }, []);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const handleLogout = async () => {
    // Stop sandbox if it's active
    if (workspace?.status === "active" || workspace?.sessionId) {
      try {
        await stopSanboxMutation.mutateAsync({
          sessionId: workspace?.sessionId || "",
        });
        resetWorkspace();
      } catch (error) {
        console.error("Failed to stop sandbox during logout:", error);
      }
    }
    queryClient.clear();
    logout();
  };

  const handleLogoClick = () => {
    if (!user) return;

    if (user.role === ROLES.STUDENT) {
      navigate(paths.root);
    } else if (
      user.role === ROLES.ADMIN ||
      user.role === ROLES.INSTRUCTOR ||
      user.role === ROLES.TA
    ) {
      navigate(paths.manageProjects);
    }
  };

  const location = useLocation();
  if (!user) return null;
  // Hide the top header on the full-screen course details page
  if (location.pathname.startsWith("/my-courses/course-details")) return null;

  return (
    <header className="fixed top-0 left-0 w-full h-[72px] z-50 border-b border-gray-300 dark:border-[#30363d] bg-white dark:bg-[#161b22] shrink-0 flex items-center justify-between px-4 transition-colors duration-200">
      <div className="flex items-center justify-between w-full h-full">
        {/* Logo and Title */}
        <div className="flex items-center space-x-4">
          <div
            className="flex items-center space-x-3 cursor-pointer"
            onClick={handleLogoClick}
          >
            <div className="w-10 h-10 relative">
              <ImageWithFallback
                src={configurationQuery?.data?.data?.logoUrl || Logo}
                alt="Data Lab Logo"
                className="w-full h-full object-contain"
              />
            </div>
            <div>
              <h1 className="font-semibold text-bits-blue dark:text-[#f0883e]">
                {configurationQuery?.data?.data?.title || "Data Lab"}
              </h1>
              {/* <p className="text-xs text-muted-foreground">
                Data Science Projects Platform
              </p> */}
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <Button
            onClick={() => {
              navigate(paths.communication, {
                state: { defaultTab: "messages", from: location.pathname },
              });
            }}
            variant="outline"
            size="sm"
            className="relative dark:border-[#30363d] dark:text-[#c9d1d9] dark:hover:bg-[#30363d]"
          >
            <MessageSquare className="h-4 w-4" />
          </Button>
          {/* Notifications */}
          <NotificationDropdown />
          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="dark:border-[#30363d] dark:text-[#c9d1d9] dark:hover:bg-[#30363d]">
                <User className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">{user.name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="dark:bg-[#161b22] dark:border-[#30363d]">
              <DropdownMenuLabel className="dark:text-[#f0f6fc]">
                <div>
                  <p className="font-medium">{user.name}</p>
                  <p className="text-xs text-muted-foreground dark:text-[#8b949e] capitalize">
                    {user.role}
                  </p>
                  <p className="text-xs text-muted-foreground dark:text-[#8b949e]">{user.email}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="dark:bg-[#30363d]" />

              {/* User Management for Admin and Instructor */}
              {user.role === ROLES.ADMIN /*|| user.role === "instructor"*/ && (
                <DropdownMenuItem onClick={() => navigate("user-management")} className="dark:text-[#c9d1d9] dark:focus:bg-[#21262d]">
                  <Users className="h-4 w-4 mr-2" />
                  User Management
                </DropdownMenuItem>
              )}

              {/* Role Permissions for Admin*/}
              {/* {user.role === "admin" && (
                <DropdownMenuItem
                  onClick={() => navigateTo("role-permissions")}
                >
                  <Users className="h-4 w-4 mr-2" />
                  Role Permissions
                </DropdownMenuItem>
              )} */}

              {user.role === ROLES.STUDENT && (
                <DropdownMenuItem onClick={() => navigate("gradebook")} className="dark:text-[#c9d1d9] dark:focus:bg-[#21262d]">
                  <TrendingUp className="h-4 w-4 mr-2" />
                  My Grades
                </DropdownMenuItem>
              )}

              {
                /*user.role === "instructor" || */ user.role ===
                  ROLES.ADMIN && (
                  <DropdownMenuItem onClick={() => navigate("gradebook")} className="dark:text-[#c9d1d9] dark:focus:bg-[#21262d]">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Gradebook
                  </DropdownMenuItem>
                )
              }

              {/* Admin-only features */}
              {user.role === ROLES.ADMIN && (
                <>
                  <DropdownMenuSeparator className="dark:bg-[#30363d]" />
                  <DropdownMenuItem onClick={() => navigate("/settings")} className="dark:text-[#c9d1d9] dark:focus:bg-[#21262d]">
                    <UserCog className="h-4 w-4 mr-2" />
                    Admin Settings
                  </DropdownMenuItem>
                </>
              )}

              {(user.role === ROLES.ADMIN || user.role === ROLES.STUDENT) && (
                <DropdownMenuSeparator className="dark:bg-[#30363d]" />
              )}

              <DropdownMenuItem onClick={handleLogout} className="text-red-600 dark:text-red-400 dark:focus:bg-[#21262d]">
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
